import { API_BASE_URL } from "src/config/api";
import React, { useCallback, useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  TextField,
  Grid,
  MenuItem,
  CircularProgress,
  Typography,
  Dialog,
  Box,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent
} from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../../../Logo/logo.png';

const Payment = () => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState('');
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [showCurrencyInfo, setShowCurrencyInfo] = useState(false);
  const [usdToInrRate, setUsdToInrRate] = useState(null);
  const originalAmount = amount ? Number(amount) : 0;
  const gstAmount = originalAmount * 0.18;
  const totalAmount = originalAmount + gstAmount;

  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();

  const showToast = (message) => {
    toast.error(message, {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const fetchUser = useCallback(async () => {
    setLoading(true);
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.get(`${API_BASE_URL}/getUserById?userId=${userId}`, config);
      setUsers(response.data.data);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, userId]);

  useEffect(() => {
    fetchUser();
    fetchExchangeRate();
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => setSdkLoaded(true);
    document.body.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (scriptToRemove) {
        document.body.removeChild(scriptToRemove);
      }
    };
  }, [fetchUser]);

  const fetchExchangeRate = async () => {
    try {
      const response = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await response.json();
      if (data.result === 'success') {
        const rate = data.rates.INR;
        setUsdToInrRate(rate);
      }
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
    }
  };

  const fetchData = async () => {
    if (!amount || !currency) {
      setErrors({
        amount: !amount ? 'Please enter an amount' : '',
        currency: !currency ? 'Please select a currency' : '',
      });
      showToast('Please enter an amount');
      return;
    }

    if (currency === 'INR') {
      setShowCurrencyInfo(true);
      return;
    }

    continueToPayment(); // proceed directly for USD
  };

  const continueToPayment = async () => {
    setLoading(true);
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const amountToPay = currency === 'INR'
        ? calculateINRPaymentAmount()
        : Math.round(totalAmount * 100);

      const response = await axios.post(
        `${API_BASE_URL}/create-order`,
        {
          amount: amountToPay,
          currency,
        },
        config
      );

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: response.data.data.amount,
        currency: response.data.data.currency,
        order_id: response.data.data.id,
        image: logo,
        name: 'Tylect Technologies Pvt Ltd',
        description: `Payment for services in ${currency}`,
        handler: (response) => {
          if (response.razorpay_payment_id) {
            handlePaymentSuccess(response.razorpay_payment_id);
          } else {
            console.log('Payment failed or cancelled');
          }
        },
      };

      if (sdkLoaded) {
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      }
    } catch (error) {
      console.error('Error creating order:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (razorpayPaymentId) => {
    await addBalanceToWallet(razorpayPaymentId);
    setErrors({});
  };

  const addBalanceToWallet = async (razorpayPaymentId) => {
    try {
      setLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const requestData = {
        id: users._id,
        paymentId: razorpayPaymentId,
        amount: Number(originalAmount),
      };
      const response = await axios.post(`${API_BASE_URL}/addBalanceToUser`, requestData, config);

      if (response.data.success) {
        navigate('/dashboard/wallet/success', { state: { showSuccess: true } });
      } else {
        console.error('Unexpected error adding balance to wallet:', response.data);
      }
    } catch (error) {
      console.error('Error adding balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateINRPaymentAmount = () => {
    const gstIncludedAmount = totalAmount; // USD
    const inrAmount = gstIncludedAmount * usdToInrRate;
    return Math.round(inrAmount * 100); // Razorpay expects paise
  };


  const handleAmountChange = (event) => {
    setAmount(event.target.value);
    setErrors((prevErrors) => ({ ...prevErrors, amount: '' }));
  };

  return (
    <>
      <Card elevation={2} sx={{ maxWidth: 500, mx: 'auto', p: 1 }}>
        <CardContent>
          <Typography variant="h5" align="center" gutterBottom>
            Add Balance
          </Typography>

          <Grid container spacing={2} mt={2}>
            <Grid item xs={12}>
              <TextField
                label="Amount in USD"
                variant="outlined"
                type="number"
                value={amount}
                onChange={handleAmountChange}
                required
                error={Boolean(errors.amount)}
                fullWidth
              />
              {errors.amount && (
                <Typography variant="caption" color="error">
                  {errors.amount}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                label="Currency"
                variant="outlined"
                value={currency}
                onChange={(event) => setCurrency(event.target.value)}
                required
                error={Boolean(errors.currency)}
                fullWidth
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="INR">INR</MenuItem>
              </TextField>
              {errors.currency && (
                <Typography variant="caption" color="error">
                  {errors.currency}
                </Typography>
              )}
            </Grid>



            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  Payment Summary
                </Typography>

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">Original Amount:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    {/* Original Amount */}
                    <Typography>
                      {currency === 'INR' && usdToInrRate
                        ? `₹${(originalAmount * usdToInrRate).toFixed(2)}`
                        : `$${originalAmount.toFixed(2)}`
                      }
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2">GST (18%):</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    {/* GST */}
                    <Typography>
                      {currency === 'INR' && usdToInrRate
                        ? `₹${(gstAmount * usdToInrRate).toFixed(2)}`
                        : `$${gstAmount.toFixed(2)}`
                      }
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2">Total Amount:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    {/* Total (Original + GST) */}
                    <Typography fontWeight="bold">
                      {currency === 'INR' && usdToInrRate
                        ? `₹${(totalAmount * usdToInrRate).toFixed(2)}`
                        : `$${totalAmount.toFixed(2)}`
                      }
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" mt={1}>
                <Button variant="contained" color="primary" onClick={fetchData} disabled={loading}>
                  {loading ? <CircularProgress size={24} /> : 'Add Balance'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Dialog to confirm conversion */}
      <Dialog
        open={showCurrencyInfo}
        onClose={() => setShowCurrencyInfo(false)}
        fullWidth
        maxWidth="xs"
        sx={{ '& .MuiDialog-paper': { p: 2, borderRadius: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center', pb: 0 }}>
          Currency Conversion Notice
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              You are making a payment in <strong>INR</strong>. After successful completion, the equivalent amount (excluding taxes) will be credited to your wallet in <strong>USD</strong>.
            </Typography>

            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Total Amount Charged:</strong> ₹{(totalAmount * usdToInrRate).toFixed(2)}<span style={{ fontSize: '0.85rem' }}> (including 18% GST)</span>
            </Typography>

            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Wallet Credit:</strong> USD equivalent of ₹{(amount * usdToInrRate).toFixed(2)}
            </Typography>

            {usdToInrRate && (
              <Typography variant="h6" color="primary" sx={{ mt: 1, fontWeight: 'bold', textAlign: 'center' }}>
                ₹{(amount * usdToInrRate).toFixed(2)} INR ≈ ${originalAmount} USD
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              GST is applied as per government regulations and is not converted to wallet credit.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 3 }}>
          <Button variant="contained" color="primary" size="small" onClick={() => {
            setShowCurrencyInfo(false);
            continueToPayment();
          }}>
            Confirm
          </Button>
          <Button variant="outlined" color="error" size="small" onClick={() => setShowCurrencyInfo(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default Payment;
